import { isEnglish } from '../lib/i18n'

/*
 * The date arithmetic under the calendars (Calendar.jsx) — no React, so it can be shared
 * and tested on its own.
 *
 * Calendar labels come from Intl, not from a hand-written table: they are locale data,
 * not product copy. Both tags carry `-u-ca-gregory-nu-latn` on purpose. Plain `ar-SA`
 * resolves to the ISLAMIC calendar in Chrome, which would print a Hijri month over a
 * Gregorian ISO value, and to Arabic-Indic digits, which would not match the Latin
 * numerals every other figure in the app uses.
 */
const LOCALE = isEnglish ? 'en-GB-u-ca-gregory-nu-latn' : 'ar-u-ca-gregory-nu-latn'
export const monthLabel = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' })
export const monthName = new Intl.DateTimeFormat(LOCALE, { month: 'long' })

/*
 * The column heads are the SHORT day name, not the narrow one: a single letter is
 * ambiguous in both scripts (two S's, two T's; an Arabic set only readable if you
 * already know the order). Arabic's short form keeps the definite article — الأحد —
 * which will not sit in a seven-column grid, so it is trimmed to أحد, the
 * abbreviation Arabic calendars actually print.
 */
const weekdayLabel = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' })
/* 2024-01-06 IS a Saturday, so it is already column 0. */
export const WEEKDAYS = Array.from({ length: 7 }, (_, index) =>
  weekdayLabel.format(new Date(2024, 0, 6 + index)).replace(/^ال/, ''),
)

/** The Saudi week starts on Saturday, which is JS weekday 6. */
const FIRST_WEEKDAY = 6
const column = (date) => (date.getDay() - FIRST_WEEKDAY + 7) % 7

export const iso = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

/** Parses `YYYY-MM-DD` as a LOCAL date — `new Date(iso)` would read it as UTC. */
export const parseIso = (value) => {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  const date = new Date(y, m - 1, d)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Reads a date as typed, in the shape the screen prints it: `DD-MM-YYYY` (`/` and `.`
 * accepted too). Rolls nothing over — 31-02 is not the 3rd of March, it is no date.
 */
export const parseTyped = (text) => {
  const match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(text.trim())
  if (!match) return null
  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
}

/** What the typing turns into: digits only, the dashes put in for the reader. */
export const maskTyped = (text) => {
  const digits = text.replace(/\D/g, '').slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('-')
}

export const today = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}
export const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1)
export const addMonths = (date, count) => new Date(date.getFullYear(), date.getMonth() + count, 1)
export const addDays = (date, count) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + count)
export const sameDay = (a, b) =>
  Boolean(a && b) && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
export const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()

/** The same day N months on, held to the last day of a shorter month. */
const shiftMonths = (date, count) => {
  const target = new Date(date.getFullYear(), date.getMonth() + count, 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last))
}

/**
 * Where a key takes the focused day, or null for a key the grid does not own.
 * Arrows follow the screen, so in RTL the left arrow is the NEXT day.
 */
export function moveDay(day, event) {
  const rtl = getComputedStyle(document.documentElement).direction === 'rtl'
  switch (event.key) {
    case 'ArrowLeft':
      return addDays(day, rtl ? 1 : -1)
    case 'ArrowRight':
      return addDays(day, rtl ? -1 : 1)
    case 'ArrowUp':
      return addDays(day, -7)
    case 'ArrowDown':
      return addDays(day, 7)
    case 'Home':
      return addDays(day, -column(day))
    case 'End':
      return addDays(day, 6 - column(day))
    case 'PageUp':
      return shiftMonths(day, event.shiftKey ? -12 : -1)
    case 'PageDown':
      return shiftMonths(day, event.shiftKey ? 12 : 1)
    default:
      return null
  }
}

/** The six-week grid a month is drawn on, leading and trailing days included. */
export function monthGrid(view) {
  const first = startOfMonth(view)
  const start = addDays(first, -column(first))
  return Array.from({ length: 42 }, (_, index) => {
    const day = addDays(start, index)
    return { day, outside: day.getMonth() !== view.getMonth() }
  })
}
